import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainingVideos = ({ trainingData, onCompleteVideo }) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoProgress, setVideoProgress] = useState({});

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'var(--color-success)';
      case 'intermediate':
        return 'var(--color-warning)';
      case 'advanced':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getDifficultyLabel = (level) => {
    switch (level) {
      case 'beginner':
        return 'Básico';
      case 'intermediate':
        return 'Intermedio';
      case 'advanced':
        return 'Avanzado';
      default:
        return 'Sin nivel';
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const handleVideoPlay = (video) => {
    setCurrentVideo(video);
    // Simulate video progress tracking
    const interval = setInterval(() => {
      setVideoProgress(prev => {
        const currentProgress = prev?.[video?.id] || 0;
        if (currentProgress >= 100) {
          clearInterval(interval);
          onCompleteVideo(video?.id);
          return { ...prev, [video?.id]: 100 };
        }
        return { ...prev, [video?.id]: currentProgress + 5 };
      });
    }, 300);
  };

  const getCompletionStats = () => {
    const totalVideos = trainingData?.modules?.reduce((acc, module) => acc + module.videos?.length, 0);
    const completedVideos = trainingData?.modules?.reduce((acc, module) => 
      acc + module.videos?.filter(video => video?.completed)?.length, 0
    );
    return { total: totalVideos, completed: completedVideos };
  };

  const stats = getCompletionStats();
  const overallProgress = Math.round((stats?.completed / stats?.total) * 100);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Videos de Capacitación
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Contenido educativo express (2-3 minutos)
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm font-medium text-foreground">
              {stats?.completed}/{stats?.total} completados
            </div>
            <div className="text-xs text-muted-foreground">
              {overallProgress}% progreso
            </div>
          </div>
          <div className="w-16 bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      </div>
      {/* Training Modules */}
      <div className="space-y-6">
        {trainingData?.modules?.map((module) => {
          const moduleCompleted = module.videos?.filter(v => v?.completed)?.length;
          const moduleProgress = Math.round((moduleCompleted / module.videos?.length) * 100);

          return (
            <div key={module.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="PlayCircle" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{module.title}</h3>
                    <p className="text-sm text-muted-foreground">{module.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">
                    {moduleCompleted}/{module.videos?.length}
                  </div>
                  <div className="w-20 bg-muted rounded-full h-2 mt-1">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${moduleProgress}%` }}
                    />
                  </div>
                </div>
              </div>
              {/* Videos Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {module.videos?.map((video) => (
                  <div key={video?.id} className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground text-sm mb-1">
                          {video?.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          {video?.description}
                        </p>
                        <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                          <span className="flex items-center space-x-1">
                            <Icon name="Clock" size={12} />
                            <span>{formatDuration(video?.duration)}</span>
                          </span>
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: `${getDifficultyColor(video?.difficulty)}20`,
                              color: getDifficultyColor(video?.difficulty)
                            }}
                          >
                            {getDifficultyLabel(video?.difficulty)}
                          </span>
                        </div>
                      </div>
                      {video?.completed && (
                        <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                      )}
                    </div>

                    {/* Video Progress */}
                    {videoProgress?.[video?.id] && !video?.completed && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Reproduciendo...</span>
                          <span className="text-xs text-muted-foreground">
                            {videoProgress?.[video?.id]}%
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1">
                          <div 
                            className="bg-accent h-1 rounded-full transition-all duration-300"
                            style={{ width: `${videoProgress?.[video?.id]}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      {!video?.completed && !videoProgress?.[video?.id] && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleVideoPlay(video)}
                          className="flex-1"
                        >
                          <Icon name="Play" size={14} className="mr-1" />
                          Reproducir
                        </Button>
                      )}
                      
                      {video?.completed && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="flex-1"
                        >
                          <Icon name="RotateCcw" size={14} className="mr-1" />
                          Ver de nuevo
                        </Button>
                      )}

                      <Button variant="ghost" size="icon">
                        <Icon name="Download" size={14} />
                      </Button>
                    </div>

                    {/* Key Points */}
                    {video?.keyPoints && video?.keyPoints?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <h5 className="text-xs font-medium text-foreground mb-2">
                          Puntos Clave:
                        </h5>
                        <ul className="space-y-1">
                          {video?.keyPoints?.map((point, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" className="mt-1" />
                              <span className="text-xs text-muted-foreground">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Video Player Modal */}
      {currentVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">
                {currentVideo?.title}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentVideo(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Video Placeholder */}
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center mb-4">
              <div className="text-center">
                <Icon name="Play" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Reproduciendo: {currentVideo?.title}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Duración: {formatDuration(currentVideo?.duration)}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {currentVideo?.description}
              </div>
              <Button
                variant="default"
                onClick={() => setCurrentVideo(null)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingVideos;